/**
 * Onboarding Steps Type Definitions
 *
 * Types for onboarding step data from the backend API and internal use.
 */

/**
 * Status values for onboarding steps (matching backend enum)
 */
export type OnboardingStepStatus = 'Created' | 'In progress' | 'Complete';

/**
 * Backend API response structure for onboarding steps
 * Uses snake_case as returned by the API
 */
export interface OnboardingStepApiResponse {
  id: number;
  thread_id: string;
  step_name: string;
  created_at: string;
  finished_at: string | null;
  status: OnboardingStepStatus;
  display_name: string;
}

/**
 * Default onboarding steps API response (without thread context)
 * Used for initial display before a thread is created
 */
export interface DefaultOnboardingStepApiResponse {
  step_name: string;
  status: OnboardingStepStatus;
  display_name: string;
}

/**
 * Internal onboarding step structure
 * Uses camelCase for internal TypeScript conventions
 *
 * Note: Dates are stored as ISO strings for Redux serialization compatibility.
 * Convert to Date objects in components when needed: new Date(step.createdAt)
 */
export interface OnboardingStep {
  id: number;
  threadId: string;
  name: string;
  displayName: string;
  status: OnboardingStepStatus;
  createdAt: string;
  finishedAt: string | null;
}

/**
 * Map API response to internal type
 * Converts snake_case to camelCase
 *
 * Note: Dates are kept as ISO strings for Redux serialization.
 * Convert to Date objects in components if needed.
 *
 * @param apiStep - Step data from API
 * @returns Internal step representation
 */
export function mapOnboardingStepFromApi(
  apiStep: OnboardingStepApiResponse
): OnboardingStep {
  return {
    id: apiStep.id,
    threadId: apiStep.thread_id,
    name: apiStep.step_name,
    displayName: apiStep.display_name,
    status: apiStep.status,
    createdAt: apiStep.created_at,
    finishedAt: apiStep.finished_at,
  };
}

/**
 * Map default API response to internal type
 * Used for default steps before a thread is created
 *
 * @param apiStep - Default step data from API
 * @param index - Index for generating a temporary ID
 * @returns Internal step representation with placeholder values
 */
export function mapDefaultOnboardingStepToInternal(
  apiStep: DefaultOnboardingStepApiResponse,
  index: number
): OnboardingStep {
  return {
    id: index + 1, // Temporary ID
    threadId: '', // No thread yet
    name: apiStep.step_name,
    displayName: apiStep.display_name,
    status: apiStep.status,
    createdAt: new Date().toISOString(), // Placeholder timestamp
    finishedAt: null,
  };
}

/**
 * Current team data structure
 * Matches the API response from GET /threads/{thread_id}/current-team
 */
export interface CurrentTeam {
  id: string;
  name: string;
  subdomain?: string;
}

/**
 * Team leader data structure
 * Matches the API response from GET /threads/{thread_id}/team-leader
 */
export interface TeamLeader {
  user_id: string;
  name: string;
  email: string;
  set_at: string;
}
